<template>
  <div class="admin-table-page">
    <div class="header-container">
      <h3>
        <strong>Admin</strong>
      </h3>
      <a-button type="primary" @click="openAddModal">
        <template #icon><PlusOutlined /></template>
        Add Account
      </a-button>
    </div>

    <a-table
      :data-source="admins"
      :columns="columns"
      :loading="isLoading"
      :row-key="record => record.id"
      :pagination="{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: admins.length,
        onChange: handlePaginationChange,
        onShowSizeChange: handlePaginationChange,
      }"
      :row-class-name="(record, index) => (index % 2 === 1 ? 'table-striped' : null)"
      class="admin-table"
    >
      <!-- Slot untuk kolom 'No' -->
      <template #bodyCell="{ column, index, record }">
        <template v-if="column.key === 'no'">
          {{ (pagination.current - 1) * pagination.pageSize + index + 1 }}
        </template>
        <template v-else-if="column.key === 'action'">
          <div class="action-buttons">
            <a-button type="primary" @click="openEditModal(record)">
              <template #icon><EditOutlined /></template>
              Edit Data
            </a-button>
            <a-button class="delete-button" @click="handleDeleteClick(record)">
              <template #icon><DeleteOutlined /></template>
              Delete Account
            </a-button>
          </div>
        </template>
      </template>
    </a-table>

    <!-- Add/Edit Modal -->
    <a-modal
      :visible="isModalVisible"
      :title="modalMode === 'add' ? 'Add Admin' : 'Edit Admin'"
      width="800px"
      :footer="null"
      :closable="true"
      :mask-closable="false"
      @cancel="handleCancel"
      centered
    >
      <a-form
        ref="formRef"
        :model="formState"
        layout="vertical"
        @finish="handleSave"
        autocomplete="off"
      >
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              label="NIP"
              name="nip"
              :rules="[{ required: true, message: 'Please input the NIP!' }]"
            >
              <a-input v-model:value="formState.nip" placeholder="Enter NIP" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              label="Full Name"
              name="fullName"
              :rules="[{ required: true, message: 'Please input the full name!' }]"
            >
              <a-input v-model:value="formState.fullName" placeholder="Enter Full Name" />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              label="Email"
              name="email"
              :rules="[
                { required: true, message: 'Please input the email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]"
            >
              <a-input
                v-model:value="formState.email"
                autocomplete="off"
                placeholder="Enter Email"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              label="Job Title"
              name="jobTitle"
              :rules="[{ required: true, message: 'Please select a job title!' }]"
            >
              <a-select
                v-model:value="formState.jobTitle"
                placeholder="Select Job Title"
                :loading="jobTitlesLoading"
              >
                <a-select-option
                  v-for="(jobTitle, index) in jobTitles"
                  :key="index"
                  :value="jobTitle"
                >
                  {{ jobTitle }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- Password fields for Add mode -->
        <a-row v-if="modalMode === 'add'" :gutter="16">
          <a-col :span="24">
            <a-form-item
              label="Password"
              name="password"
              :rules="[{ required: true, message: 'Please input the password!' }]"
            >
              <a-input-password
                v-model:value="formState.password"
                autocomplete="new-password"
                placeholder="Enter Password"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <!-- Password fields for Edit mode -->
        <a-row v-if="modalMode === 'edit'" :gutter="16">
          <a-col :span="12">
            <a-form-item label="New Password" name="newPassword">
              <a-input-password
                v-model:value="formState.newPassword"
                autocomplete="off"
                placeholder="Enter New Password"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Old Password" name="oldPassword">
              <a-input-password
                v-model:value="formState.oldPassword"
                autocomplete="new-password"
                placeholder="Enter Old Password"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <div class="modal-footer">
          <a-button
            @click="handleCancel"
            size="large"
            style="color: #4C749F; border-radius: 4px; width: 84px;"
          >
            Cancel
          </a-button>
          <a-button
            type="primary"
            html-type="submit"
            style="background-color: #436894; color: white; border-radius: 4px; width: 84px;"
            size="large"
          >
            Save
          </a-button>
        </div>
      </a-form>
    </a-modal>

    <!-- Delete Confirmation Modal -->
    <a-modal v-model:visible="deleteModalVisible" title="Delete Admin" @ok="handleDelete" centered>
      Are you sure you want to delete this admin account?
    </a-modal>

    <!-- Cancel Confirmation Modal -->
    <a-modal
      v-model:visible="cancelConfirmVisible"
      title="Unsaved Changes"
      @ok="confirmCancel"
      @cancel="cancelConfirmVisible = false"
      centered
    >
      <div style="display: flex; align-items: center; margin-bottom: 16px;">
        <span>You have unsaved changes. Are you sure you want to close without saving?</span>
      </div>
      <p>
        All changes will be lost if you continue.
      </p>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { notification } from 'ant-design-vue'
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons-vue'
import { UserManagementAPI } from '@/services/axios/istry/pathwayService'

export default {
  name: 'AdminTablePage',
  components: {
    EditOutlined,
    PlusOutlined,
    DeleteOutlined,
  },
  setup() {
    const admins = ref([])
    const isLoading = ref(true)
    const isModalVisible = ref(false)
    const deleteModalVisible = ref(false)
    const cancelConfirmVisible = ref(false)
    const modalMode = ref('add')
    const editingAdminId = ref(null)
    const formRef = ref()
    const jobTitles = ref([])
    const jobTitlesLoading = ref(false)

    const initialFormState = {
      nip: '',
      fullName: '',
      email: '',
      jobTitle: null,
      password: '',
      newPassword: '',
      oldPassword: '',
    }

    const formState = reactive({ ...initialFormState })
    const originalFormState = ref({ ...initialFormState })

    // Track if form has changes
    const hasFormChanges = computed(() => {
      return JSON.stringify(formState) !== JSON.stringify(originalFormState.value)
    })

    const pagination = ref({
      current: 1,
      pageSize: 5,
    })

    const columns = [
      {
        title: 'No',
        key: 'no',
        width: 60,
        slots: { customRender: 'bodyCell' },
      },
      {
        title: 'NIP',
        dataIndex: 'nip',
        key: 'nip',
        sorter: (a, b) => a.nip.localeCompare(b.nip),
      },
      {
        title: 'Full Name',
        dataIndex: 'fullName',
        key: 'fullName',
        sorter: (a, b) => a.fullName.localeCompare(b.fullName),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        sorter: (a, b) => a.email.localeCompare(b.email),
      },
      {
        title: 'Job Title',
        dataIndex: 'jobTitle',
        key: 'jobTitle',
        sorter: (a, b) => a.jobTitle.localeCompare(b.jobTitle),
      },
      {
        title: 'Action',
        key: 'action',
        width: 300,
        slots: { customRender: 'bodyCell' },
      },
    ]

    // Fetch admin data
    const fetchAdmins = async () => {
      try {
        isLoading.value = true
        const data = await UserManagementAPI.getAllUsers('admin')
        console.log('data admin', data)
        admins.value = Array.isArray(data?.data) ? data.data : []
      } catch (error) {
        notification.error({
          message: 'Error',
          description: 'Could not fetch admin data.',
        })
        admins.value = []
      } finally {
        isLoading.value = false
      }
    }

    // Fetch job titles data
    const fetchJobTitles = async () => {
      try {
        jobTitlesLoading.value = true
        const data = await UserManagementAPI.getAllJobTitles()
        console.log('data job titles', data)
        jobTitles.value = Array.isArray(data?.data) ? data.data : []
      } catch (error) {
        notification.error({
          message: 'Error',
          description: 'Could not fetch job titles.',
        })
        jobTitles.value = []
      } finally {
        jobTitlesLoading.value = false
      }
    }

    // Modal functions
    const openAddModal = () => {
      modalMode.value = 'add'
      Object.assign(formState, initialFormState)
      originalFormState.value = { ...initialFormState }
      isModalVisible.value = true
      // Clear any previous validation errors
      if (formRef.value) {
        formRef.value.clearValidate()
      }
    }

    const openEditModal = record => {
      modalMode.value = 'edit'
      editingAdminId.value = record.id
      const editFormState = {
        nip: record.nip,
        fullName: record.fullName,
        email: record.email,
        jobTitle: record.jobTitle,
        password: '',
        newPassword: '',
        oldPassword: '',
      }
      Object.assign(formState, editFormState)
      originalFormState.value = { ...editFormState }
      isModalVisible.value = true
      // Clear any previous validation errors
      if (formRef.value) {
        formRef.value.clearValidate()
      }
    }

    const handleCancel = () => {
      if (hasFormChanges.value) {
        cancelConfirmVisible.value = true
      } else {
        closeModal()
      }
    }

    const confirmCancel = () => {
      cancelConfirmVisible.value = false
      closeModal()
    }

    const closeModal = () => {
      isModalVisible.value = false
      // Clear form validation errors
      if (formRef.value) {
        formRef.value.clearValidate()
      }
      // Reset form state
      Object.assign(formState, initialFormState)
      originalFormState.value = { ...initialFormState }
    }

    const handleSave = async () => {
      try {
        await formRef.value.validate()

        if (modalMode.value === 'add') {
          // Add new admin
          const adminData = {
            nip: formState.nip,
            fullName: formState.fullName,
            email: formState.email,
            jobTitle: formState.jobTitle,
            password: formState.password,
          }

          await UserManagementAPI.createAdmin(adminData)

          notification.success({
            message: 'Success',
            description: 'New admin added successfully.',
          })
        } else {
          // Update admin
          const updateData = {
            id: editingAdminId.value,
            nip: formState.nip,
            fullName: formState.fullName,
            email: formState.email,
            jobTitle: formState.jobTitle,
          }

          // Add password fields only if they are provided
          if (formState.newPassword) {
            updateData.newPassword = formState.newPassword
          }
          if (formState.oldPassword) {
            updateData.oldPassword = formState.oldPassword
          }

          await UserManagementAPI.updateAdmin(updateData)

          notification.success({
            message: 'Success',
            description: 'Admin updated successfully.',
          })
        }

        closeModal()
        fetchAdmins()
      } catch (error) {
        notification.error({
          message: 'Save Failed',
          description: 'Please check the form for errors.',
        })
      }
    }

    const handleDeleteClick = record => {
      editingAdminId.value = record.id
      deleteModalVisible.value = true
    }

    const handleDelete = async () => {
      try {
        await UserManagementAPI.deleteAdmin(editingAdminId.value)
        notification.success({
          message: 'Success',
          description: 'Admin has been deleted successfully.',
        })

        await fetchAdmins()
      } catch (error) {
        notification.error({
          message: 'Delete Failed',
          description: error.response?.data?.message || 'An error occurred.',
        })
      } finally {
        deleteModalVisible.value = false
      }
    }

    const handlePaginationChange = (page, pageSize) => {
      pagination.value.current = page
      if (pageSize) {
        pagination.value.pageSize = pageSize
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    onMounted(() => {
      fetchAdmins()
      fetchJobTitles()
    })

    return {
      admins,
      isLoading,
      isModalVisible,
      deleteModalVisible,
      cancelConfirmVisible,
      modalMode,
      editingAdminId,
      formRef,
      formState,
      pagination,
      columns,
      jobTitles,
      jobTitlesLoading,
      hasFormChanges,
      openAddModal,
      openEditModal,
      handleCancel,
      confirmCancel,
      handleSave,
      handleDeleteClick,
      handleDelete,
      handlePaginationChange,
    }
  },
}
</script>

<style scoped>
.admin-table-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.admin-table {
  border-radius: 12px;
  overflow: hidden;
  color: #395b86;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.delete-button {
  background-color: #f9c6c6;
  color: #9f4351;
}

.delete-button:hover {
  background-color: #ed5757;
  color: #ffffff;
}

.modal-footer {
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

:deep(.ant-table-tbody > tr > td) {
  border-bottom: 1px solid #e3f0fa;
}

:deep(.ant-table-tbody > tr > td:nth-child(even)) {
  background-color: #f7fbfe;
}

:deep(.ant-table-thead > tr > th:nth-child(odd)) {
  background-color: #f2faff !important;
  font-weight: 600;
  color: #395b86;
}

:deep(.ant-table-thead > tr > th:nth-child(even)) {
  background-color: #e6f4fc !important;
  font-weight: 600;
  color: #395b86;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background-color: #e6f7ff !important;
}

:deep(.ant-table-tbody > tr > td),
:deep(.ant-table-thead > tr > th) {
  border-right: none;
  border-left: none;
}
</style>
